import {
  CommunicationType,
  PhoneType,
  DayOfWeek
} from '@grc/ui-shared/lib/components/models/enums/enums';
import { ICommunicationDetail } from '@grc/ui-shared/lib/components/models/accounts/account';
import {
  IAddressInformation,
  IBusinessHours,
  ICarrierBasicInfo,
  ICarrierPortalSettings,
  ICarrierSettingDetails,
  IContactColumns,
  IContactFormValues,
  IDailySchedule,
  IEmergencyContactInfo,
  IEquipment,
  IGeoFence,
  IIntegrationDetails,
  ILocationInformation,
  IPaymentAndInvoicing,
  IPreAssignedReferences,
  IScheduleType,
  ITenderContactInfo,
  ITenderSetting,
  ITimeInterval,
  ITracking
} from '../models/settings.interface';

export const initialTracking: ITracking[] = [
  {
    trackingMethod: undefined,
    trackingMethodPriority: undefined,
    trackingReferenceType: undefined,
    checkCallInterval: undefined
  },
  {
    trackingMethod: undefined,
    trackingMethodPriority: undefined,
    trackingReferenceType: undefined,
    checkCallInterval: undefined
  }
];

export const intialPreAssignedReferences: IPreAssignedReferences = {
  proNumbers: [{ from: '', to: '' }]
};

export const defaultTimeInterval: ITimeInterval[] = [
  {
    openTime: '00:00:00',
    closeTime: '00:00:00'
  }
];

export const defaultScheduleDay: IDailySchedule = {
  isDayOff: false,
  timeIntervals: defaultTimeInterval
};

export const defaultScheduleType: IScheduleType = {
  isCustomWorkingHours: false,
  isOpen24Hours: false,
  dailySchedules: {
    [DayOfWeek.Sunday]: defaultScheduleDay,
    [DayOfWeek.Monday]: defaultScheduleDay,
    [DayOfWeek.Tuesday]: defaultScheduleDay,
    [DayOfWeek.Wednesday]: defaultScheduleDay,
    [DayOfWeek.Thursday]: defaultScheduleDay,
    [DayOfWeek.Friday]: defaultScheduleDay,
    [DayOfWeek.Saturday]: defaultScheduleDay
  }
};

export const initialBusinessHours: IBusinessHours = {
  id: '',
  businessHours: defaultScheduleType,
  shippingHours: defaultScheduleType,
  receivingHours: defaultScheduleType,
  isSaturday: false,
  isSunday: false
};

export const initialCommunicationDetail: ICommunicationDetail = {
  communicationType: CommunicationType.Mobile,
  communicationAddressType: PhoneType.Home,
  contactValue: '',
  displayOrder: 0
};

export const initialAddressInformation: IAddressInformation = {
  companyName: '',
  addressLine: [],
  city: '',
  state: '',
  zip: '',
  country: '',
  latitude: '',
  longitude: '',
  timeZone: ''
};

export const initialLocationInformation: ILocationInformation = {
  locationEmail: '',
  telephoneNos: [
    {
      communicationType: CommunicationType.Mobile,
      communicationAddressType: PhoneType.Home,
      contactValue: '',
      displayOrder: 0
    }
  ],
  websiteURL: '',
  branchCode: '',
  country: '',
  timeZone: '',
  displayId: '',
  displayName: ''
};

export const initialCarrierBasicInfo: ICarrierBasicInfo = {
  scac: '',
  mc: '',
  dotIcc: '',
  federalTaxID: '',
  caatCode: '',
  svii: '',
  cbsa: '',
  modesSupported: [
    {
      id: '',
      name: ''
    }
  ],
  hazmatAuthorized: false
};

export const initialEmergencyContactInfo: IEmergencyContactInfo = {
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactEmail: '',
  hazmatAuthorized: false
};

export const initialCarrierSettingDetails: ICarrierSettingDetails = {
  addressInformation: initialAddressInformation,
  locationInformation: initialLocationInformation,
  carrierBasicInfo: initialCarrierBasicInfo,
  emergencyContactInfo: initialEmergencyContactInfo
};

export const initialContactFormValues: IContactFormValues = {
  avatarUrl: '',
  firstName: '',
  middleName: '',
  lastName: '',
  nickName: '',
  locationName: '',
  title: '',
  department: '',
  address: initialAddressInformation,
  city: '',
  telephoneCountryCode: 'US',
  telephoneNumber: '',
  phoneNumbers: [
    {
      communicationType: CommunicationType.Mobile,
      communicationAddressType: PhoneType.Home,
      contactValue: '',
      displayOrder: 1
    }
  ],
  email: ''
};

export const initialGeoFence: IGeoFence = {
  id: '',
  name: '',
  fence: '',
  checkCallType: 0,
  description: '',
  displayColor: '#387ADF'
};

export const initialEquipment: IEquipment = {
  id: '',
  numberOfTractorsOrPowerUnits: 0,
  numberOfTeamDrivers: 0,
  numberOfSoloDrivers: 0,
  maxWeightByEquipmentType: { Unit: '', Value: 0 },
  maxWeightForCarrier: { Unit: '', Value: 0 },
  maxPalletCountForCarrier: 0,
  equipmentTypeName: '',
  equipmentTypes: [
    {
      equipmentTypeId: '',
      numberOfUnits: 0
    }
  ],
  isActive: true
};

export const initialAccountInformation: ITenderContactInfo = {
  email: '',
  telephoneNos: [
    {
      contactValue: '',
      communicationType: CommunicationType.Mobile,
      communicationAddressType: PhoneType.Mobile,
      displayOrder: 1
    }
  ]
};

export const initialTenderSetting: ITenderSetting = {
  id: '',
  transportationModeId: '',
  tenderResponsiveTime: '',
  tenderMethods: undefined,
  tenderContactId: '',
  tenderContactName: '',
  advanceTenderNotificationInDays: '',
  tenderContactInfo: initialAccountInformation,
  isAutoAccept: true,
  tenderWindowTime: defaultScheduleType,
  tenderRateType: null
};

export const initialIntegrationDetails: IIntegrationDetails = {
  isTrackingApiSetup: false,
  isTrackingEdiSetup: false,
  isEdiInvoicingConfigured: false,
  isApiInvoicingConfigured: false,
  isApiIntegratedWithOtherSystems: false,
  isEdiConfiguredWithOtherSystems: false
};

export const initialContactsList: IContactColumns[] = [];

export const initialPaymentAndInvoicing: IPaymentAndInvoicing = {
  locationId: '',
  invoiceMethod: 'Edi',
  invoicePartyName: '',
  invoiceLocation: '',
  thirdPartyFreightPaymentCompany: '',
  responsibleLocationId: '',
  billToId: '',
  thirdPartyLocationId: '',
  creditLimit: 0,
  creditDays: 0,
  financialStabilityScore: 0,
  billGenerationTerm: '15Days'
};

export const initialCarrierPortalSettingsState: ICarrierPortalSettings = {
  tracking: initialTracking,
  preAssignedReferences: intialPreAssignedReferences,
  businessHours: initialBusinessHours,
  carrierSettingDetails: initialCarrierSettingDetails,
  contacts: initialContactFormValues,
  contactsList: initialContactsList,
  geoFences: initialGeoFence,
  equipment: [initialEquipment],
  tenderSettings: [initialTenderSetting],
  integrationDetails: initialIntegrationDetails,
  paymentAndInvoicing: initialPaymentAndInvoicing
};
